import {
  getAttributeKeyLabel,
  getNodeLabel,
  getProjectData,
  getRelationkeyLabel,
  getUid,
  WeaverQuery
} from 'Services/WeaverUtils'
import moment from 'moment'
import { Tag } from 'antd'

export selectionTypes = {
  ROW: 'ROW'
  COL: 'COL'
  CELL: 'CELL'
  MULTI_CELL: 'MULTI_CELL'
  MULTI_ROW: 'MULTI_ROW'
  MULTI_COL: 'MULTI_COL'
}

tableContexts = {
  NODE: 'NODE'
  TYPES: 'TYPES'
}

idColumn = {
  key: 'id'
  label: 'Identifier',
  renderFn: (node) -> node.id()
  createWithId: (id) -> id
}

getDefaultAttrColConf = (key) ->
  {
    key,
    label: getAttributeKeyLabel(key),
    renderFn: (n) ->
      n.get(key)
    editFn: (node, val) ->
      if val is '' or !val?
        node.unset(key)
      else
        node.set(key, val, { dataType: node.getDataType(key) })
      node
  }

buildDefaultAttributeColumns = (nodes) ->

  [firstAttribute] = attrKeys = _.uniq(nodes.map((n) ->
    _.keys(n.attributes())
  ).flat())

  if firstAttribute?
    { nameKeys } = getProjectData()
    nameCols = _.remove(attrKeys, (i) -> nameKeys.indexOf(i) isnt -1 )
      .sort((a, b) ->
        nameKeys.indexOf(a) - nameKeys.indexOf(b)
      )
    [nameCols..., attrKeys...].map(getDefaultAttrColConf)
  else
    []

export emptyCell = (col) ->
  if col.relationConf
    []
  else
    ''

getDefaultRelColConf = (key) ->
  {
    key,
    label: getRelationkeyLabel(key),
    relationConf: {
      key
      targetLabel: 'Name'
      targetValidator: (q) -> q
    }
    renderFn: (node) ->
      node
        .relation(key)
        .all()
        .map(getUid)
    editFn: (node, targets) ->
      rel = node.relation(key)
      rel.clearSync()
      targets.map((target) ->
        rel.onlyOnceSync(target) if target?
      )
      node
  }

buildDefaultRelationColumns = (nodes) ->
  relKeys = _.uniq(nodes.map((n) ->
    _.keys(n.relations())
  ).flat())

  relationColumns = relKeys.map(getDefaultRelColConf)

export parseDateOrLeaveEmpty = (contents) ->
  if contents is '' or !contents? then '' else moment(contents)

export defaultColConf = (nodes, showIdColumn) ->

  attributeColumns = buildDefaultAttributeColumns(nodes)
  relationColumns = buildDefaultRelationColumns(nodes)

  if showIdColumn
    cols = [idColumn].concat(attributeColumns).concat(relationColumns)
  else
    attributeColumns.concat(relationColumns)

export loadRelationTargets = ({ nodes, columns, relationsMap }) ->
  targets = columns.map(({ relationConf: { key } }) ->
    nodes.map((n) ->
      n.relation(key)
        .all()
        .map((n) -> n.id())
    ).flat()
  ).flat()

  fetchRelations(targets, relationsMap)

export fetchRelations = (targets, relationsMap) ->

  results = await WeaverQuery()
    .restrict(
      _.uniq(targets).filter((id) ->
        _.keys(relationsMap).indexOf(id) is -1
      )
    ).withRelations().find()

  {
    results.reduce((acc, n) ->
      { acc..., [getUid(n)]: n }
    , {})...
    relationsMap...
  }

createHiddenTag = ->
  tag = document.createElement("div")
  tag.style.position = "absolute"
  tag.style.left = "-999em"
  tag.style.whiteSpace = "nowrap"
  tag.style.font = "12px Open Sans"

  document.body.appendChild(tag)
  tag

tag = createHiddenTag()

checkHeaderWidths = (columns) ->
  columns.map(({ label }, j) ->
    tag.innerHTML = _.truncate(label, { length: 40 })
    tag.clientWidth + 80 # + 80 is for the filter icon to the right of the header + cell-padding
  )

getSizeOfRelationCell = ({ node, key, relationsMap }) ->
  if firstRelation = node.relation(key).first()
    tag.innerHTML = getNodeLabel(
      relationsMap[getUid(firstRelation)]
    )

    # 65 = width + padding of the `+ n ...` tag which indicates `n` additional relations
    # 40 = the padding for the tag

    (tag.clientWidth + 40) + 65
  else
    100

getSizeOfAttributeCell = (node, renderFn) ->
  text = (
    if renderFn(node)?
      renderFn(node)
    else
      ''
  )
  tag.innerHTML = _.truncate(text, { length: 40 })

  size = tag.clientWidth + 70

checkCellWidths = (columns, tableData, relationsMap) ->
  result = []

  _.values(tableData).map((node) ->
    columns.map(({ label, relationConf, renderFn, key }, j) ->
      if relationConf?
        size = getSizeOfRelationCell({ node, key, relationsMap })
      else
        size = getSizeOfAttributeCell(node, renderFn)
      result[j] = Math.max(size, (result[j] or 0))
    )
  )
  result

stretchLastColumn = (widths, min) ->
  margin = 65
  [total, lastIndex] = _.reduce(widths, (([acc], val, i) -> [acc + val, i]), [0])

  stretchAmount = Math.max(min - total - margin, 0)
  {
    widths...
    [lastIndex]: widths[lastIndex] + stretchAmount
  }

export recalculateColumnWidths = ({ columns, tableData, relationsMap, width }) ->

  headerWidths = checkHeaderWidths(columns)
  largestCellWidths = checkCellWidths(columns, tableData, relationsMap)
  mergedWidths = headerWidths.map((h, i) ->
    Math.max(h, largestCellWidths[i] or 0)
  )

  stretchLastColumn(mergedWidths, width)

export getPasteMatrix = (matrix, target, [rowOffset, colOffset], isRepeatingVal = false, columns) ->

  toIterate = matrix
  relationTargets = []

  if isRepeatingVal
    value = matrix[0][0]
    toIterate = target

  formattedData = toIterate.reduce((acc, currRow, i) ->
    { acc..., [i + rowOffset]: currRow.reduce((acc, cellContents, j) ->
      colIndex = j + colOffset
      next = value or cellContents

      if columns[colIndex]?.relationConf
        next = next?.split?(',') or []
        relationTargets.push(next...)

      { acc..., [colIndex]: next }
    , {}) }
  , {})

  { formattedData, relationTargets }

export filterReadOnly = (data, columns) ->
  _.mapValues(data, (row) ->
    _.pickBy(row, (cell, key) ->
      columns[key]? and (columns[key].editFn? or columns[key].createWithId? or columns[key].relationConf?)
    )
  )

export getSelectionMatrix = (selection, selectionType, rowCount, colCount) ->
  switch selectionType
    when selectionTypes.ROW
      [new Array(colCount).fill('')]
    when selectionTypes.COL
      new Array(rowCount).fill([''])
    when selectionTypes.CELL
      [['']]
    when selectionTypes.MULTI_CELL
      [[x1, y1], [x2, y2]] = selection
      new Array(Math.abs(y1 - y2) + 1)
        .fill(
          new Array(Math.abs(x1 - x2) + 1)
            .fill('')
        )
    when selectionTypes.MULTI_ROW
      [x, y] = selection
      new Array(Math.abs(x - y) + 1).fill(new Array(colCount).fill(''))
    when selectionTypes.MULTI_COL
      [x, y] = selection
      new Array(rowCount).fill(new Array(Math.abs(x - y) + 1).fill(''))

export getBoundingBox = (selection, selectionType) ->
  switch selectionType
    when selectionTypes.ROW
      [0, selection, undefined, selection + 1]
    when selectionTypes.COL
      [selection, 0, selection + 1, undefined]
    when selectionTypes.CELL
      [selection[0], selection[1], selection[0] + 1, selection[1] + 1]
    when selectionTypes.MULTI_CELL
      [
        Math.min(selection[0][0], selection[1][0]),
        Math.min(selection[0][1], selection[1][1]),
        Math.max(selection[0][0], selection[1][0]) + 1,
        Math.max(selection[0][1], selection[1][1]) + 1
      ]
    when selectionTypes.MULTI_ROW
      [0, Math.min(selection[0], selection[1]), undefined, Math.max(selection[0], selection[1]) + 1]
    when selectionTypes.MULTI_COL
      [Math.min(selection[0], selection[1]), 0, Math.max(selection[0], selection[1]) + 1, undefined]

export getPasteOffset = (selection, selectionType) ->
  switch selectionType
    when selectionTypes.ROW
      [selection, 0]
    when selectionTypes.COL
      [0, selection]
    when selectionTypes.CELL
      [selection[1], selection[0]]
    when selectionTypes.MULTI_CELL
      [
        Math.min(selection[0][1], selection[1][1]),
        Math.min(selection[0][0], selection[1][0])
      ]
    when selectionTypes.MULTI_ROW
      [Math.min(selection[0], selection[1]), 0]
    when selectionTypes.MULTI_COL
      [0, Math.min(selection[0], selection[1])]

export getAffectedRows = (selection, selectionType, totalRows) ->
  switch selectionType
    when selectionTypes.ROW
      [selection, selection]
    when selectionTypes.COL
      [0, totalRows]
    when selectionTypes.CELL
      [selection[1], selection[1]]
    when selectionTypes.MULTI_CELL
      [
        Math.min(selection[0][1], selection[1][1]),
        Math.max(selection[0][1], selection[1][1])
      ]
    when selectionTypes.MULTI_ROW
      [
        Math.min(selection[0], selection[1]),
        Math.max(selection[0], selection[1])
      ]
    when selectionTypes.MULTI_COL
      [0, totalRows]

export getCellValues = (nodes, columns, edits) ->
  foundEmptyRow = false
  cellData = nodes.map((n, i) ->
    columns.map(({ renderFn }, j) ->
      if edits[i]?[j]
        edits[i][j]
      else if n
        renderFn(n)
      else
        foundEmptyRow = true
        ''
    ).map((val) -> val?.replace?(/[\r\n\t]/g, " "))
  )
  { cellData, foundEmptyRow }
